import React, { useEffect } from "react";

import { useIntl } from "react-intl";
import { Route, Routes, useNavigate, useParams } from "react-router-dom";

import { CloseButton } from "ds/Button";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { domainMatches } from "Libs/utils";
import { addIntegration, getIntegrations } from "Reducers/integration";
import { gitProjectSelector } from "Reducers/project/project";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import IntegrationForm from "../components/IntegrationForm/Pages";

import Stepper from "./components/Stepper";
import IntegrationsList from "./containers/IntegrationsList";
import * as S from "./Setup.styles";

const Setup = () => {
  const { organizationId, projectId } = useParams<{
    organizationId: string;
    projectId: string;
  }>();

  const dispatch = useAppDispatch();
  const intl = useIntl();
  const navigate = useNavigate();

  const project = useSelectorWithUrlParams(gitProjectSelector);
  const lastEdited = useAppSelector(state => state.integration.lastEdited);
  const status = useAppSelector(state => state.integration.status);

  const INTEGRATIONS_ROUTE = `/${organizationId}/${projectId}/-/settings/integrations`;

  useEffect(() => {
    if (projectId) dispatch(getIntegrations({ projectId }));
  }, [dispatch, projectId]);

  useEffect(() => {
    if (status === "added") navigate(INTEGRATIONS_ROUTE + "/" + lastEdited);
  }, [INTEGRATIONS_ROUTE, navigate, lastEdited, status]);

  const onSubmit = (inputs: Record<string, unknown>, type: string = "") => {
    // type use health. on the api object but we use health_ on the url
    if (
      type.toLowerCase() === "github" &&
      domainMatches("github.com", inputs.base_url as string)
    ) {
      //Pretend the user did not enter any input if the domain matches github.com
      delete inputs.base_url;
    }

    if (!project) throw new Error("Project not found");

    dispatch(
      addIntegration({
        project,
        type: type.replace("health_", "health."),
        data: inputs
      })
    );
  };

  return (
    <S.Layout>
      <S.Wrapper>
        <S.Header>
          <S.AddIntegrationTitle tag="h2">
            {intl.formatMessage({
              id: "integration.setup.title",
              defaultMessage: "add integration"
            })}
          </S.AddIntegrationTitle>
          <Stepper />
          <CloseButton to={INTEGRATIONS_ROUTE}>
            {intl.formatMessage({ id: "cancel" })}
          </CloseButton>
        </S.Header>

        <S.Content>
          <Routes>
            <Route
              path=":type/configure"
              element={
                <>
                  <S.IntegrationTitle tag="h3">
                    {intl.formatMessage({
                      id: "integration.setup.configure",
                      defaultMessage: "Configure your integration"
                    })}
                  </S.IntegrationTitle>

                  <IntegrationForm
                    onSubmit={onSubmit}
                    integrationAction="create"
                  />
                </>
              }
            />
            <Route
              path=""
              element={
                <>
                  <S.IntegrationTitle tag="h3">
                    {intl.formatMessage({
                      id: "integration.setup.select",
                      defaultMessage: "Select integration"
                    })}
                  </S.IntegrationTitle>

                  <IntegrationsList />
                </>
              }
            />
          </Routes>
        </S.Content>
      </S.Wrapper>
    </S.Layout>
  );
};

export default Setup;
